import { useState } from "react";
import { Copy, Check, Heart, Building2, Users, HandCoins, QrCode } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ORG, DONATION, IMAGES } from "../data/site";

function CopyRow({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-brand-line bg-brand-cream px-5 py-4">
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-[0.16em] text-brand-muted">{label}</p>
        <p className="mt-1 font-medium text-brand-ink truncate">{value}</p>
      </div>
      <button onClick={copy} data-testid={testid} aria-label={`Copy ${label}`} className="grid place-items-center h-10 w-10 shrink-0 rounded-lg bg-brand-primary/10 text-brand-primary hover:bg-brand-primary hover:text-brand-cream transition-colors">
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

export default function GetInvolved() {
  return (
    <>
      <PageHeader
        eyebrow="Get Involved"
        title="Be Part of the Change"
        subtitle="Give, partner or fundraise — every contribution reaches families in Bishnupur District directly and transparently."
        image={IMAGES.women}
      />

      <section id="donate" className="scroll-mt-24 bg-brand-cream">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
          <Tabs defaultValue="donate" className="w-full">
            <TabsList data-testid="involve-tabs" className="mx-auto flex w-full max-w-lg rounded-full bg-brand-surface p-1.5 h-auto">
              <TabsTrigger value="donate" data-testid="tab-donate" className="flex-1 rounded-full py-2.5 text-sm data-[state=active]:bg-brand-primary data-[state=active]:text-brand-cream">Donate</TabsTrigger>
              <TabsTrigger value="partner" data-testid="tab-partner" className="flex-1 rounded-full py-2.5 text-sm data-[state=active]:bg-brand-primary data-[state=active]:text-brand-cream">Partner</TabsTrigger>
              <TabsTrigger value="fundraise" data-testid="tab-fundraise" className="flex-1 rounded-full py-2.5 text-sm data-[state=active]:bg-brand-primary data-[state=active]:text-brand-cream">Fundraise</TabsTrigger>
            </TabsList>

            {/* Donate */}
            <TabsContent value="donate" className="mt-12">
              <div className="grid gap-10 lg:grid-cols-2">
                <Reveal>
                  <span className="text-xs uppercase tracking-[0.24em] text-brand-primary">Your gift at work</span>
                  <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-brand-ink">Choose an impact</h2>
                  <div className="mt-8 space-y-4">
                    {DONATION.tiers.map((t, i) => (
                      <div key={i} data-testid={`donation-tier-${i}`} className="flex items-center gap-5 rounded-2xl border border-brand-line bg-brand-surface p-5">
                        <span className="font-serif text-2xl text-brand-primary min-w-[90px]">{t.amount}</span>
                        <span className="text-sm text-brand-muted">{t.impact}</span>
                      </div>
                    ))}
                  </div>
                </Reveal>

                <Reveal delay={0.1}>
                  <div className="rounded-3xl border border-brand-line bg-brand-surface p-8">
                    <div className="flex items-center gap-3 text-brand-primary">
                      <QrCode className="h-6 w-6" />
                      <h3 className="font-serif text-2xl text-brand-ink">Give directly</h3>
                    </div>
                    <p className="mt-3 text-sm text-brand-muted">Use the UPI ID or bank details below. Do share your transfer screenshot with us so we can thank you and issue a receipt.</p>

                    <div className="mt-7 space-y-3">
                      <CopyRow label="UPI ID" value={DONATION.upi} testid="copy-upi" />
                      <CopyRow label="Account Name" value={DONATION.bank.accountName} testid="copy-acc-name" />
                      <CopyRow label="Account Number" value={DONATION.bank.accountNumber} testid="copy-acc-number" />
                      <CopyRow label="IFSC" value={DONATION.bank.ifsc} testid="copy-ifsc" />
                      <CopyRow label="Bank" value={DONATION.bank.bankName} testid="copy-bank" />
                    </div>
                    {/* <-- REPLACE placeholder UPI / bank details with real NGO payment info --> */}
                    <a href={`mailto:${ORG.email}?subject=Donation%20Confirmation`} data-testid="donation-confirm-mail" className="mt-7 flex items-center justify-center gap-2 rounded-full bg-brand-primary px-6 py-3.5 font-semibold text-brand-cream hover:bg-brand-primaryHover transition-colors">
                      <Heart className="h-5 w-5" /> Email us your confirmation
                    </a>
                  </div>
                </Reveal>
              </div>
            </TabsContent>

            {/* Partner */}
            <TabsContent value="partner" id="partner" className="mt-12 scroll-mt-24">
              <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
                <Reveal>
                  <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-primary/10 text-brand-primary"><Building2 className="h-7 w-7" /></div>
                  <h2 className="mt-6 font-serif text-3xl sm:text-4xl text-brand-ink">Corporate & institutional partnerships</h2>
                  <p className="mt-5 text-brand-muted leading-relaxed">Align your CSR goals with proven grassroots impact. We collaborate on skills training, health, child welfare and environmental programs — with transparent reporting.</p>
                  <a href={`mailto:${ORG.email}?subject=Partnership%20Enquiry`} data-testid="partner-mail" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-primary px-7 py-3.5 font-semibold text-brand-cream hover:bg-brand-primaryHover transition-colors">
                    <Building2 className="h-5 w-5" /> Discuss a partnership
                  </a>
                </Reveal>
                <Reveal delay={0.1}>
                  <div className="overflow-hidden rounded-3xl"><img src={IMAGES.seminar} alt="Community partnership workshop" className="aspect-[4/3] w-full object-cover" /></div>
                </Reveal>
              </div>
            </TabsContent>

            {/* Fundraise */}
            <TabsContent value="fundraise" id="fundraise" className="mt-12 scroll-mt-24">
              <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
                <Reveal delay={0.1} className="lg:order-2">
                  <div className="overflow-hidden rounded-3xl"><img src={IMAGES.sports} alt="Community fundraising event" className="aspect-[4/3] w-full object-cover" /></div>
                </Reveal>
                <Reveal className="lg:order-1">
                  <div className="grid place-items-center h-14 w-14 rounded-2xl bg-brand-primary/10 text-brand-primary"><HandCoins className="h-7 w-7" /></div>
                  <h2 className="mt-6 font-serif text-3xl sm:text-4xl text-brand-ink">Fundraise for us</h2>
                  <p className="mt-5 text-brand-muted leading-relaxed">Birthdays, marathons, community drives — turn your moment into meaning. Rally your circle to fund a program you care about, and we&rsquo;ll support you every step.</p>
                  <ul className="mt-6 space-y-3">
                    {["Pick a program to support", "We share stories & materials", "Celebrate the impact together"].map((s, i) => (
                      <li key={i} className="flex items-center gap-3 text-brand-muted"><span className="grid place-items-center h-7 w-7 rounded-full bg-brand-accent text-brand-ink text-sm font-semibold">{i + 1}</span>{s}</li>
                    ))}
                  </ul>
                  <a href={`mailto:${ORG.email}?subject=Fundraising%20with%20Equipping%20New%20Life`} data-testid="fundraise-mail" className="mt-8 inline-flex items-center gap-2 rounded-full bg-brand-primary px-7 py-3.5 font-semibold text-brand-cream hover:bg-brand-primaryHover transition-colors">
                    <Users className="h-5 w-5" /> Start fundraising
                  </a>
                </Reveal>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </>
  );
}
